import { useCallback } from 'react';
import {
    AudioConfigParams,
    FiltersParams,
    NavigationParams,
} from 'types/params';
import { FilterName, RoutePaths } from 'types/enums';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { useAuthentication } from 'components/AuthenticationProvider/AuthenticationProvider';
import { useCreateUserRecentSearch } from 'apollo/userRecentSearch/useCreateUserRecentSearch';
import { ModifiersPayload } from '../types';
import getUpdatedPathname from '../helpers/getUpdatedPathname';
import { useSearchController } from '../SearchContext';
import { useGetCurrentSearchParams } from './useCurrentSearchState';

const isArrayFiltersParams = (filterKey: FiltersParams) => {
    switch (filterKey) {
        case FiltersParams.genres:
        case FiltersParams.instruments:
        case FiltersParams.sfxs:
        case FiltersParams.tags:
            return true;

        default:
            return false;
    }
};

export const useSearchModifiers = () => {
    const { isAuthenticated } = useAuthentication();

    const createUserRecentSearch = useCreateUserRecentSearch({
        isAuthenticated,
    });

    const getCurrentSearchParams = useGetCurrentSearchParams();
    const { pathname: locationPathname } = useLocation();
    const { baseUrl } = useSearchController();
    const pathname = baseUrl ?? locationPathname;
    const navigate = useNavigate();
    const routeParams = useParams();

    const toLocalizedPath = useToLocalizedPath();

    const browsePath = toLocalizedPath(`/${RoutePaths.Browse}`);

    const updateSearch = useCallback(
        (searchParams: URLSearchParams) => {
            const instrumentsFromRoute = routeParams[FilterName.Instruments];
            const genresFromRoute = routeParams[FilterName.Genres];
            const sfxsFromRoute = routeParams[FilterName.Sfxs];

            // if seo routes is found, and the corresponding search params is still there
            // remove the search params and use the seo routes
            // if not, use the browse routes
            const updatedPathname = getUpdatedPathname({
                pathname,
                browsePath,
                searchParams,
                instrumentsFromRoute,
                genresFromRoute,
                sfxsFromRoute,
            });

            // delete page when we update search
            searchParams.delete(NavigationParams.page);

            // TODO - https://mixgenius.atlassian.net/browse/SP-10142
            searchParams.delete(AudioConfigParams.autoPlay);

            navigate({
                pathname: updatedPathname,
                search: searchParams.toString(),
            });
        },
        [navigate, routeParams, pathname, browsePath],
    );

    const addFilter = useCallback(
        (modifiers: ModifiersPayload[]): void => {
            const searchParams = getCurrentSearchParams();

            createUserRecentSearch(modifiers);

            modifiers.forEach(([filterKey, value]: ModifiersPayload) => {
                if (!value) {
                    return;
                }

                if (isArrayFiltersParams(filterKey)) {
                    searchParams.append(filterKey, value);
                } else if (filterKey === FiltersParams.textsearch) {
                    const currentTextSearch = searchParams.get(
                        FiltersParams.textsearch,
                    );

                    const newTextSearch = currentTextSearch
                        ? `${currentTextSearch},${value}`
                        : value;

                    searchParams.set(FiltersParams.textsearch, newTextSearch);
                } else {
                    searchParams.set(filterKey, value);
                }
            });

            updateSearch(searchParams);
        },
        [updateSearch, getCurrentSearchParams, createUserRecentSearch],
    );

    const clearAll = useCallback((): void => {
        const searchParams = getCurrentSearchParams();

        for (const filterKey in FiltersParams) {
            if (filterKey) {
                searchParams.delete(filterKey);
            }
        }

        updateSearch(searchParams);
    }, [updateSearch, getCurrentSearchParams]);

    const clearByFilter = useCallback(
        (type: FiltersParams) => {
            const searchParams = getCurrentSearchParams();

            searchParams.delete(type);

            updateSearch(searchParams);
        },
        [updateSearch, getCurrentSearchParams],
    );

    const removeFilter = useCallback(
        (modifiers: ModifiersPayload[]): void => {
            const searchParams = getCurrentSearchParams();

            modifiers.forEach(([filterKey, value]: ModifiersPayload) => {
                if (filterKey === FiltersParams.textsearch) {
                    const filteredTextSearch = searchParams
                        .get(FiltersParams.textsearch)
                        ?.split(',')
                        .filter((tag) => tag !== value)
                        .join(',');

                    searchParams.delete(FiltersParams.textsearch);

                    filteredTextSearch &&
                        searchParams.set(
                            FiltersParams.textsearch,
                            filteredTextSearch,
                        );
                } else {
                    const tags = searchParams
                        .getAll(filterKey)
                        .filter((tag) => tag !== value);

                    searchParams.delete(filterKey);
                    for (const tag of tags) searchParams.append(filterKey, tag);
                }
            });

            updateSearch(searchParams);
        },
        [updateSearch, getCurrentSearchParams],
    );

    return { addFilter, clearAll, clearByFilter, removeFilter };
};
